import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import axios from 'axios';
import themeAssure from './theme';

/**
 * Racine de l'API vue depuis ce téléphone.
 *
 * `REACT_APP_API_URL` pointe sur « localhost » en développement — or pour le
 * téléphone, localhost est le téléphone. On garde le port et le chemin
 * configurés, mais on prend l'hôte qui a servi cette page : c'est le seul
 * joignable depuis ici.
 */
export function racineApi() {
  const configuree = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/';
  try {
    const url = new URL(configuree, window.location.origin);
    if (url.hostname !== window.location.hostname) {
      url.hostname = window.location.hostname;
      url.protocol = window.location.protocol;
    }
    return url.toString().replace(/\/$/, '');
  } catch {
    return configuree.replace(/\/$/, '');
  }
}

const RACINE = racineApi();

const minutesSecondes = (s) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * La page qu'ouvre le téléphone après avoir scanné le QR.
 *
 * Volontairement dépouillée, et sans authentification : le porteur du jeton
 * n'a pas de compte. En contrepartie elle ne montre rien du dossier — ni son
 * nom, ni ce qu'il contient — seulement un bouton et un compteur.
 */
export default function PageDepot() {
  const { jeton } = useParams();
  const [etat, setEtat] = useState(null);
  const [restant, setRestant] = useState(0);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [envoyees, setEnvoyees] = useState(0);

  const url = `${RACINE}/depot/${jeton}/`;

  const rafraichir = useCallback(async () => {
    try {
      const { data } = await axios.get(url);
      setEtat(data);
      setRestant(data.secondes_restantes);
    } catch (e) {
      setErreur(e?.response?.status === 404
        ? "Ce lien n'existe pas ou a été fermé."
        : 'Impossible de contacter le serveur.');
    }
  }, [url]);

  useEffect(() => { rafraichir(); }, [rafraichir]);

  useEffect(() => {
    if (restant <= 0) return undefined;
    const t = setInterval(() => setRestant((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [restant]);

  const envoyer = async (fichiers) => {
    setEnvoi(true);
    setErreur('');
    try {
      for (const f of fichiers) {
        const charge = new FormData();
        charge.append('fichier', f);
        const { data } = await axios.post(url, charge);
        setEtat(data);
        setRestant(data.secondes_restantes);
        setEnvoyees((n) => n + 1);
      }
    } catch (e) {
      setErreur(e?.response?.data?.detail || "L'envoi n'a pas abouti.");
    } finally {
      setEnvoi(false);
    }
  };

  const expire = etat && (!etat.valide || restant <= 0);

  return (
    <ThemeProvider theme={themeAssure}>
      <ScopedCssBaseline>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
          <Container maxWidth="sm">
            <Typography variant="h1" sx={{ mb: 1 }}>
              Vos photos
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Prenez vos documents en photo. Ils rejoindront votre dossier sur
              l'ordinateur, sans rien à saisir ici.
            </Typography>

            {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}
            {!etat && !erreur && <CircularProgress size={28} />}

            {etat && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {expire ? (
                    <Alert severity="warning">
                      {etat.motif || 'Ce lien a expiré.'} Affichez un nouveau
                      code sur votre ordinateur.
                    </Alert>
                  ) : (
                    <>
                      <Box>
                        <Stack direction="row" justifyContent="space-between"
                               sx={{ mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Temps restant
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {minutesSecondes(restant)}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (restant / 3600) * 100)}
                        />
                      </Box>

                      <Button component="label" variant="contained" size="large"
                              disabled={envoi} fullWidth>
                        {envoi ? 'Envoi en cours…' : 'Prendre une photo'}
                        <input type="file" hidden accept="image/*"
                               capture="environment"
                               onChange={(e) => envoyer([...e.target.files])} />
                      </Button>

                      <Button component="label" variant="outlined"
                              disabled={envoi} fullWidth>
                        Choisir dans la galerie
                        <input type="file" hidden multiple
                               accept="image/*,application/pdf"
                               onChange={(e) => envoyer([...e.target.files])} />
                      </Button>
                    </>
                  )}

                  <Typography variant="body2" color="text.secondary"
                              sx={{ textAlign: 'center' }}>
                    {envoyees === 0
                      ? 'Aucune photo envoyée.'
                      : `${envoyees} photo${envoyees > 1 ? 's' : ''} envoyée${envoyees > 1 ? 's' : ''}.`}
                    {' '}
                    {etat.pieces_max ? `Maximum ${etat.pieces_max}.` : ''}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Container>
        </Box>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}
